import React, { useEffect } from "react";
import { DateTime } from "luxon";

import {
  Typography,
  Box,
  Card,
  Grid2 as Grid,
  List,
  ListItem,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";

const NotaryJobNotice = ({
  currentUser,
  handleSetCompleted,
  infoModal,
  setInfoModal,
  closestJobDate,
  fiveMinBeforeSession,
  elapsed,
  setElapsed,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const getElapsedTime = (closestIso) => {
    // Convert closestIso to DateTime object in UTC
    const closestDateUTC = DateTime.fromISO(closestIso, { zone: "utc" });
    // Get the current time in UTC
    const nowUTC = DateTime.utc();

    // Calculate the difference
    const duration = nowUTC.diff(closestDateUTC, ["hours", "minutes"]);

    // Extract hours and minutes
    const hours = duration.hours;
    const minutes = duration.minutes;

    setElapsed({ hours: Math.floor(hours), minutes: Math.floor(minutes) });
    // Return as a formatted string
  };

  useEffect(() => {
    if (currentUser?.closest_job?.booked_date) {
      getElapsedTime(currentUser?.closest_job?.booked_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval;
    if (currentUser?.closest_job?.booked_date && fiveMinBeforeSession) {
      interval = setInterval(() => {
        getElapsedTime(currentUser?.closest_job?.booked_date);
      }, 5000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiveMinBeforeSession, currentUser?.closest_job?.booked_date]);

  return (
    <Card
      sx={{
        backgroundColor: "#FCF2F0",
        borderRadius: {
          xs: 2, // Mobile: smaller border radius
          sm: 3, // Tablet and up: original border radius
        },
      }}
    >
      <Grid
        container
        px={{
          xs: 2, // Mobile: smaller horizontal padding
          sm: 4, // Tablet: medium padding
          md: 6, // Desktop: original padding
        }}
        sx={{
          mt: {
            xs: 2, // Mobile: smaller top margin
            sm: 3, // Tablet: medium margin
            md: 4, // Desktop: original margin
          },
        }}
      >
        <Grid size={{ xs: 12 }}>
          <Typography
            color="primary"
            align={fiveMinBeforeSession ? "center" : "left"}
            variant={isMobile ? "h4" : "h3"}
            sx={{
              fontSize: {
                xs: "1.5rem", // Mobile: smaller title
                sm: "2rem", // Tablet: medium title
                md: "3rem", // Desktop: original h3 size
              },
              lineHeight: 1.2,
              mb: {
                xs: 2, // Mobile: smaller bottom margin
                sm: 3, // Tablet and up: original margin
              },
            }}
          >
            {fiveMinBeforeSession
              ? "RON Session is Started..."
              : "Next Notarization Appointment"}
          </Typography>
          {fiveMinBeforeSession && (
            <>
              {elapsed.minutes < 0 ? (
                <Typography
                  color="primary"
                  variant={isMobile ? "body2" : "body1"}
                  fontWeight="bold"
                  align="center"
                  sx={{
                    fontSize: {
                      xs: "0.875rem", // Mobile: smaller text
                      sm: "1rem", // Tablet and up: original size
                    },
                    px: {
                      xs: 2, // Mobile: add horizontal padding for better text wrapping
                      sm: 0, // Tablet and up: no extra padding
                    },
                  }}
                >
                  You may join the session up to 5 minutes before the scheduled
                  start time
                </Typography>
              ) : (
                <Typography
                  align="center"
                  variant={isMobile ? "body1" : "h6"}
                  mt={2}
                  color="GrayText"
                  sx={{
                    fontSize: {
                      xs: "1rem", // Mobile: smaller text
                      sm: "1.25rem", // Tablet and up: original h6 size
                    },
                  }}
                >
                  Elapsed Time Since Scheduled RON Session:
                </Typography>
              )}
            </>
          )}
          {fiveMinBeforeSession && (
            <>
              <Box align="center" mt={1}>
                {elapsed.minutes >= 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 0,
                      mb: {
                        xs: 2, // Mobile: smaller bottom margin
                        sm: 3, // Tablet and up: original margin
                      },
                      gap: {
                        xs: 0.25, // Mobile: smaller gap
                        sm: 0.5, // Tablet and up: original gap
                      },
                    }}
                  >
                    {elapsed.hours > 0 && (
                      <>
                        <Typography
                          color="error"
                          variant="h3"
                          sx={{
                            fontSize: {
                              xs: "2rem", // Mobile: smaller timer
                              sm: "2.5rem", // Tablet: medium timer
                              md: "3rem", // Desktop: original h3 size
                            },
                          }}
                        >
                          {elapsed.hours}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="error"
                          sx={{
                            fontSize: {
                              xs: "0.875rem", // Mobile: smaller label
                              sm: "1rem", // Tablet and up: original size
                            },
                          }}
                        >
                          {elapsed.hours > 1 ? "Hours" : "Hour"}
                        </Typography>
                      </>
                    )}

                    <Typography
                      sx={{
                        ml: {
                          xs: 1, // Mobile: smaller left margin
                          sm: 2, // Tablet and up: original margin
                        },
                        color:
                          (elapsed.hours === 0 || !elapsed.hours) &&
                          elapsed.minutes <= 15
                            ? "#10b981"
                            : (elapsed.hours === 0 || !elapsed.hours) &&
                                elapsed.minutes > 15 &&
                                elapsed.minutes <= 45
                              ? "#ff9800"
                              : "#f04438",
                        fontSize: {
                          xs: "2rem", // Mobile: smaller timer
                          sm: "2.5rem", // Tablet: medium timer
                          md: "3rem", // Desktop: original h3 size
                        },
                      }}
                      variant="h3"
                    >
                      {elapsed.minutes < 10
                        ? `0${elapsed.minutes}`
                        : elapsed.minutes}
                    </Typography>
                    <Typography
                      sx={{
                        color:
                          (elapsed.hours === 0 || !elapsed.hours) &&
                          elapsed.minutes <= 15
                            ? "#10b981"
                            : (elapsed.hours === 0 || !elapsed.hours) &&
                                elapsed.minutes > 15 &&
                                elapsed.minutes <= 45
                              ? "#ff9800"
                              : "#f04438",
                        fontSize: {
                          xs: "0.875rem", // Mobile: smaller label
                          sm: "1rem", // Tablet and up: original size
                        },
                      }}
                      variant="body1"
                      fontWeight="bold"
                    >
                      Min
                    </Typography>
                  </Box>
                )}
                <Button
                  disabled={
                    infoModal.rescheduleLoading ||
                    infoModal.completedLoading ||
                    infoModal.voidLoading
                  }
                  component="a"
                  href={
                    currentUser?.closest_job?.notarization?.notarization
                      ?.bn_session?.notary_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: {
                      xs: "100%", // Mobile: full width
                      sm: "20rem", // Tablet and up: fixed width
                    },
                    minHeight: {
                      xs: "48px", // Mobile: touch-friendly height
                      sm: "36px", // Tablet and up: standard height
                    },
                    fontSize: {
                      xs: "1rem", // Mobile: larger text
                      sm: "0.875rem", // Tablet and up: standard text
                    },
                  }}
                  variant="contained"
                >
                  Join Session
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column", // Mobile: stack buttons vertically
                    sm: "row", // Tablet and up: horizontal layout
                  },
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  pt: 1,
                  gap: {
                    xs: 2, // Mobile: larger gap between stacked buttons
                    sm: 2, // Tablet and up: original gap
                  },
                  borderTop: "3px solid #d3d3d3",
                }}
              >
                {/* <Button
                  disabled={
                    infoModal.rescheduleLoading ||
                    infoModal.completedLoading ||
                    infoModal.voidLoading
                  }
                  // onClick={() => setInfoModal({ ...infoModal, logsOpen: true })}
                  size="small"
                  color="warning"
                  variant="contained"
                >
                  LOGS
                </Button> */}
                <LoadingButton
                  onClick={() => {
                    setInfoModal({ ...infoModal, rescheduleOpen: true });
                    getElapsedTime(currentUser?.closest_job?.booked_date);
                  }}
                  loading={infoModal.rescheduleLoading}
                  disabled={infoModal.completedLoading || infoModal.voidLoading}
                  size={isMobile ? "medium" : "small"}
                  color="secondary"
                  variant="contained"
                  sx={{
                    width: {
                      xs: "100%", // Mobile: full width
                      sm: "auto", // Tablet and up: auto width
                    },
                    minHeight: {
                      xs: "48px", // Mobile: touch-friendly height
                      sm: "32px", // Tablet and up: small button height
                    },
                    fontSize: {
                      xs: "0.875rem", // Mobile: readable text
                      sm: "0.75rem", // Tablet and up: small button text
                    },
                  }}
                >
                  MARK AS EXPIRED
                </LoadingButton>
                <LoadingButton
                  loading={infoModal.completedLoading}
                  disabled={
                    infoModal.rescheduleLoading || infoModal.voidLoading
                  }
                  onClick={handleSetCompleted}
                  size={isMobile ? "medium" : "small"}
                  color="success"
                  variant="contained"
                  sx={{
                    width: {
                      xs: "100%", // Mobile: full width
                      sm: "auto", // Tablet and up: auto width
                    },
                    minHeight: {
                      xs: "48px", // Mobile: touch-friendly height
                      sm: "32px", // Tablet and up: small button height
                    },
                    fontSize: {
                      xs: "0.875rem", // Mobile: readable text
                      sm: "0.75rem", // Tablet and up: small button text
                    },
                  }}
                >
                  MARK AS COMPLETED
                </LoadingButton>
              </Box>
            </>
          )}
        </Grid>

        <>
          <Grid
            size={{ xs: 12, sm: 8 }}
            sx={{
              pl: {
                xs: 0, // Mobile: no left padding
                sm: 4, // Tablet: medium padding
                md: "6rem", // Desktop: original padding
              },
              pt: {
                xs: 2, // Mobile: smaller top padding
                sm: 3, // Tablet and up: original padding
              },
              pb: 0,
            }}
          >
            {currentUser?.closest_job === "N/A" ? (
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{
                  mt: {
                    xs: 2, // Mobile: smaller top margin
                    sm: 3, // Tablet and up: original margin
                  },
                  fontSize: {
                    xs: "1rem", // Mobile: smaller text
                    sm: "1.25rem", // Tablet and up: original h6 size
                  },
                  lineHeight: 1.4,
                }}
              >
                There are no appointments scheduled at the moment. Please wait
                until you receive invitations to require notarization by email
                and text message.
              </Typography>
            ) : (
              <>
                <List>
                  <ListItem sx={{ paddingLeft: 0, textAlign: "center" }}>
                    <Typography
                      component="div"
                      variant={isMobile ? "h6" : "h5"}
                      sx={{
                        display: "block",
                        fontSize: {
                          xs: "1.25rem", // Mobile: smaller title
                          sm: "1.5rem", // Tablet and up: original h5 size
                        },
                      }}
                    >
                      Date and Time
                      <Typography
                        component="div"
                        sx={{
                          fontWeight: "bold",
                          color: "#3b663c",
                          fontSize: {
                            xs: "1rem", // Mobile: smaller date text
                            sm: "1.25rem", // Tablet and up: original size
                          },
                          wordBreak: "break-word", // Prevent text overflow
                        }}
                      >
                        {closestJobDate}
                      </Typography>
                    </Typography>
                  </ListItem>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column", // Mobile: stack vertically
                        sm: "row", // Tablet and up: horizontal layout
                      },
                    }}
                  >
                    <ListItem sx={{ paddingLeft: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "block" }}
                      >
                        <Typography
                          variant={isMobile ? "body1" : "h6"}
                          sx={{
                            fontSize: {
                              xs: "1rem", // Mobile: smaller heading
                              sm: "1.25rem", // Tablet and up: original h6 size
                            },
                            fontWeight: {
                              xs: 600, // Mobile: semibold
                              sm: 700, // Tablet and up: bold
                            },
                          }}
                        >
                          Signer
                        </Typography>

                        <Stack direction="column">
                          <Typography component="span" variant="body2">
                            {currentUser?.closest_job?.signer?.includes(
                              "assigning",
                            )
                              ? "Contractor"
                              : currentUser?.closest_job?.signer
                                  .charAt(0)
                                  .toUpperCase() +
                                currentUser?.closest_job?.signer.slice(1)}
                          </Typography>
                        </Stack>
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ paddingLeft: 0, maxWidth: "353.33px" }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "block" }}
                      >
                        <Typography variant="h6">Document Type</Typography>
                        <Stack direction="column">
                          <Typography component="span" variant="body2">
                            Permit Package
                          </Typography>
                          <Typography component="span" variant="body2">
                            {
                              currentUser?.closest_job?.document
                                ?.property_address
                            }
                          </Typography>
                          <Typography component="span" variant="body2">
                            {currentUser?.closest_job?.document?.county}
                          </Typography>
                          <Typography
                            component="a"
                            href={currentUser?.closest_job?.document?.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {currentUser?.closest_job?.document?.pdf}
                          </Typography>
                        </Stack>
                      </Typography>
                    </ListItem>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column", // Mobile: stack vertically
                        sm: "row", // Tablet and up: horizontal layout
                      },
                    }}
                  >
                    <ListItem sx={{ paddingLeft: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "block" }}
                      >
                        <Typography
                          variant={isMobile ? "body1" : "h6"}
                          sx={{
                            fontSize: {
                              xs: "1rem", // Mobile: smaller heading
                              sm: "1.25rem", // Tablet and up: original h6 size
                            },
                            fontWeight: {
                              xs: 600, // Mobile: semibold
                              sm: 700, // Tablet and up: bold
                            },
                          }}
                        >
                          Signer Details
                        </Typography>
                        <Stack direction="column">
                          <Typography component="span" variant="body2">
                            {`${
                              currentUser?.closest_job?.signer_details?.first ||
                              ""
                            } ${
                              currentUser?.closest_job?.signer_details
                                ?.middle || ""
                            } ${
                              currentUser?.closest_job?.signer_details?.last ||
                              ""
                            }`
                              .trim()
                              .replace(/\s+/g, " ")}
                          </Typography>
                          <Typography component="span" variant="body2">
                            {`${
                              currentUser?.closest_job?.signer_details
                                ?.address || ""
                            } ${
                              currentUser?.closest_job?.signer_details?.city ||
                              ""
                            } ${
                              currentUser?.closest_job?.signer_details?.state ||
                              ""
                            }  ${
                              currentUser?.closest_job?.signer_details?.zip ||
                              ""
                            }`
                              .trim()
                              .replace(/\s+/g, " ")}
                          </Typography>
                          <Typography component="span" variant="body2">
                            {currentUser?.closest_job?.signer_details?.mobile}
                          </Typography>
                          <Link href="mailto:martin-w@ww.com" color="inherit">
                            <Typography component="span" variant="body2">
                              {currentUser?.closest_job?.signer_details?.email}
                            </Typography>
                          </Link>
                        </Stack>
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ paddingLeft: 0, maxWidth: "353.33px" }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "block" }}
                      >
                        <Typography variant="h6">Selected Documents</Typography>
                        <Stack
                          direction="column"
                          sx={{ height: "6rem", overflow: "auto" }}
                        >
                          {currentUser?.closest_job?.notarization?.notarization?.selected_documents?.map(
                            (document, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                }}
                              >
                                <Typography component="span" variant="body2">
                                  <Typography component="span" variant="body2">
                                    {document?.name
                                      ?.replace(/_/g, " ")
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase(),
                                      )}
                                  </Typography>
                                </Typography>
                              </Box>
                            ),
                          )}
                        </Stack>
                      </Typography>
                    </ListItem>
                  </Box>
                </List>
              </>
            )}
          </Grid>
        </>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              mt: {
                xs: 2, // Mobile: add top margin
                sm: 0, // Tablet and up: no top margin
              },
            }}
          >
            <img
              src="/img/logos/stamp.png"
              style={{
                width: isMobile ? "40%" : "60%", // Mobile: smaller image
                height: "auto",
                maxWidth: "200px", // Prevent image from being too large on mobile
              }}
              alt="Notary Public Stamp"
            />
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default NotaryJobNotice;
